import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { PiCamera } from 'react-icons/pi'
import to from 'await-to-js'
import { useMountEffect } from 'hooks/useMountEffect'
import useApiService from 'services/api'
import { Button } from '@firstbite/ui'
import { MobileModal } from 'components/UI/Modal/MobileModal'

type Mode = 'idle' | 'camera' | 'preview'

export default function MobileCaptureCard() {
  const navigate = useNavigate()
  const api = useApiService()
  const uploadInputRef = useRef<HTMLInputElement>(null)
  // Native file-input camera — fallback for environments without getUserMedia
  // (e.g. mobile Safari served over plain http, where the live camera API is
  // blocked but the OS camera via a file input still works).
  const cameraInputRef = useRef<HTMLInputElement>(null)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  // canvas.toBlob is async; if the user hits Cancel between Capture and the
  // callback firing, this flag lets the in-flight callback bail instead of
  // flipping the mode back to 'preview' after we've returned to 'idle'.
  const captureCancelledRef = useRef(false)
  const [mode, setMode] = useState<Mode>('idle')
  const [file, setFile] = useState<File | undefined>()
  const [previewUrl, setPreviewUrl] = useState<string | undefined>()
  const [isExtracting, setIsExtracting] = useState(false)

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop())
    streamRef.current = null
  }

  const previewUrlRef = useRef<string | undefined>(undefined)
  // Keep previewUrlRef in sync with previewUrl state so the unmount cleanup can
  // revoke the blob URL — the ref survives into the cleanup closure, state does
  // not. Always set the preview through this helper, never setPreviewUrl directly.
  const updatePreviewUrl = (url: string | undefined) => {
    setPreviewUrl(url)
    previewUrlRef.current = url
  }

  useMountEffect(() => () => {
    stopStream()
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current)
    }
  })

  const startCamera = async () => {
    const getUserMedia = navigator.mediaDevices?.getUserMedia
    if (!getUserMedia) {
      // No live camera API (insecure context / old browser): use the native
      // OS camera via the file input instead.
      cameraInputRef.current?.click()
      return
    }
    const [err, stream] = await to(
      navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: false,
      })
    )
    if (err || !stream) {
      toast.error(
        'Could not access the camera. You can upload an image instead.'
      )
      return
    }
    streamRef.current = stream
    setMode('camera')
  }

  // Callback ref: attach the stream once the <video> mounts (avoids an effect).
  const attachVideo = (node: HTMLVideoElement | null) => {
    videoRef.current = node
    if (node && streamRef.current) {
      node.srcObject = streamRef.current
      void node.play()
    }
  }

  const capturePhoto = () => {
    const video = videoRef.current
    if (!video || !video.videoWidth) return
    captureCancelledRef.current = false
    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
    canvas.toBlob(
      (blob) => {
        // Bail if the user cancelled while toBlob was in flight.
        if (captureCancelledRef.current || !blob) return
        setFile(new File([blob], 'card.jpg', { type: 'image/jpeg' }))
        updatePreviewUrl(URL.createObjectURL(blob))
        stopStream()
        setMode('preview')
      },
      'image/jpeg',
      0.92
    )
  }

  const cancelCamera = () => {
    captureCancelledRef.current = true
    stopStream()
    setMode('idle')
  }

  const handleSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0]
    if (!selected) return
    setFile(selected)
    updatePreviewUrl(URL.createObjectURL(selected))
    setMode('preview')
  }

  const reset = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl)
    }
    setFile(undefined)
    updatePreviewUrl(undefined)
    setMode('idle')
  }

  const handleUse = async () => {
    if (!file) return
    setIsExtracting(true)
    const [err, prefill] = await to(api.captureContactFromImage(file))
    setIsExtracting(false)
    if (err || !prefill) {
      toast.error('Could not read the card. Enter the details manually.')
      navigate('/contacts/create/manual')
      return
    }
    navigate('/contacts/create/manual', {
      state: { prefill, companyQuery: prefill.company_name },
    })
  }

  const renderFooter = () => {
    if (mode === 'camera') {
      return (
        <div className="flex gap-4 p-2 justify-between">
          <div className="w-1/2">
            <Button
              className="w-full"
              type="button"
              size="lg"
              variant="primary"
              appearance="outline"
              onClick={cancelCamera}
            >
              Cancel
            </Button>
          </div>
          <div className="w-1/2">
            <Button
              className="w-full"
              type="button"
              size="lg"
              variant="primary"
              onClick={capturePhoto}
            >
              Capture
            </Button>
          </div>
        </div>
      )
    }
    if (mode === 'preview') {
      return (
        <div className="flex gap-4 p-2 justify-between">
          <div className="w-1/2">
            <Button
              className="w-full"
              type="button"
              size="lg"
              variant="primary"
              appearance="outline"
              onClick={reset}
              disabled={isExtracting}
            >
              Replace
            </Button>
          </div>
          <div className="w-1/2">
            <Button
              className="w-full"
              type="button"
              size="lg"
              variant="primary"
              onClick={handleUse}
              disabled={isExtracting}
            >
              {isExtracting ? 'Reading…' : 'Use photo'}
            </Button>
          </div>
        </div>
      )
    }
    return <div />
  }

  return (
    <MobileModal
      handleBack={() => navigate(-1)}
      header={
        <div className="p-2">
          <div className="text-xs uppercase tracking-wide text-fg-tertiary mb-1">
            Create a contact
          </div>
          <h1 className="text-2xl font-semibold text-fg">Scan a contact</h1>
          <div className="text-xs text-fg-tertiary mt-2">
            Snap a business card, event badge, or handwritten note. We will
            figure out the rest.
          </div>
        </div>
      }
      footer={renderFooter()}
    >
      <div className="flex flex-col items-center justify-center p-6 gap-4">
        <input
          ref={cameraInputRef}
          data-testid="card-camera-input"
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={handleSelect}
        />
        <input
          ref={uploadInputRef}
          data-testid="card-upload-input"
          type="file"
          accept="image/*,.heic,.heif"
          className="hidden"
          onChange={handleSelect}
        />

        {mode === 'camera' && (
          <video
            ref={attachVideo}
            autoPlay
            playsInline
            muted
            className="max-h-80 w-full rounded-xl border border-line bg-black object-contain"
          />
        )}

        {mode === 'preview' && previewUrl && (
          <img
            src={previewUrl}
            alt="Captured card"
            className="max-h-80 rounded-xl border border-line"
          />
        )}

        {mode === 'idle' && (
          <div className="flex w-full flex-col items-center gap-4">
            <Button
              className="w-full"
              type="button"
              size="lg"
              variant="primary"
              onClick={startCamera}
            >
              <PiCamera />
              Take a photo
            </Button>
            <Button
              type="button"
              variant="primary"
              appearance="outline"
              className="w-full"
              onClick={() => uploadInputRef.current?.click()}
            >
              Upload a photo instead
            </Button>
          </div>
        )}
      </div>
    </MobileModal>
  )
}
